import { useCallback, useEffect, useMemo, useRef } from "react";
import { Html, useAnimations, useGLTF } from "@react-three/drei";
import { useFrame, type ThreeEvent } from "@react-three/fiber";
import * as THREE from "three";
import { SkeletonUtils } from "three-stdlib";

import { useAgentSync } from "@/hooks/useAgentSync";
import { useBlendTree } from "@/hooks/useBlendTree";
import { createPokeController, type ClawbertState } from "@/lib/pokeLines";
import { useEngineStore } from "@/store/useEngineStore";
import { DEFAULT_ENGINE_CONFIG, type AgentStatus } from "@/types/engine.types";

/**
 * Which line pool a poke draws from — the ambient layer we can derive locally:
 * night by the clock, working while he's on the move, idle otherwise. The
 * substantive states (stackReady, converted, lowCredits, returning) come from
 * the real agent event log and get wired in once that reaches the engine store.
 */
function deriveClawbertState(status: AgentStatus): ClawbertState {
  const hour = new Date().getHours();
  if (hour >= 22 || hour < 6) return "night";
  if (status === "moving") return "working";
  return "idle";
}

const LINE_HOLD_MS = 3400;

interface LobsterAgentProps {
  onPointerDown?: (event: ThreeEvent<PointerEvent>) => void;
}

export function LobsterAgent({ onPointerDown }: LobsterAgentProps) {
  useAgentSync();

  // Poke bark: one controller per mount holds the cooldown + no-repeat memory.
  // The bubble text lives in the engine store (I2: engine render data is store-only).
  const pokeRef = useRef(createPokeController());
  const lineTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (lineTimer.current) clearTimeout(lineTimer.current); }, []);

  const handlePoke = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      // Left-click / primary tap only, matching the selection handler.
      if (event.nativeEvent.button === 0) {
        const { agents, setPokeLine } = useEngineStore.getState();
        const status = agents["claw-1"]?.status ?? "idle";
        const said = pokeRef.current.poke(deriveClawbertState(status));
        // null = he's had enough and ignores you; clear the bubble so the
        // silence lands as the punchline.
        setPokeLine({ agentId: "claw-1", line: said });
        if (lineTimer.current) clearTimeout(lineTimer.current);
        if (said) {
          lineTimer.current = setTimeout(
            () => useEngineStore.getState().setPokeLine({ agentId: "claw-1", line: null }),
            LINE_HOLD_MS,
          );
        }
      }
      // Preserve selection + poke.wav from the RTS handler.
      onPointerDown?.(event);
    },
    [onPointerDown],
  );

  const { scene: meshScene } = useGLTF("/assets/lobster/lobster-mesh.glb");
  const { animations } = useGLTF("/assets/lobster/lobster-animations.glb");
  const clonedScene = useMemo(() => SkeletonUtils.clone(meshScene), [meshScene]);
  const { actions, mixer } = useAnimations(animations, clonedScene);

  const agent = useEngineStore((s) => s.agents["claw-1"]);
  const line = useEngineStore((s) => s.pokeLines["claw-1"] ?? null);

  useBlendTree({ actions, mixer, agentId: "claw-1" });

  useFrame((_, delta) => {
    const currentAgent = useEngineStore.getState().agents["claw-1"];
    if (!currentAgent || currentAgent.status !== "moving") return;

    const { path, pathIndex } = currentAgent;
    if (pathIndex >= path.length) {
      useEngineStore.getState().clearMoveTarget({ agentId: "claw-1" });
      useEngineStore.getState().setAgentStatus({ id: "claw-1", status: "idle" });
      return;
    }

    const target = new THREE.Vector3(...path[pathIndex]);
    const current = new THREE.Vector3(...currentAgent.position);
    const direction = target.clone().sub(current);
    const distance = direction.length();

    if (distance < 0.1) {
      const nextIndex = pathIndex + 1;
      if (nextIndex >= path.length) {
        useEngineStore.getState().clearMoveTarget({ agentId: "claw-1" });
        useEngineStore
          .getState()
          .setAgentStatus({ id: "claw-1", status: "idle" });
      } else {
        useEngineStore.getState().updateAgentPosition({
          id: "claw-1",
          position: currentAgent.position,
          rotation: currentAgent.rotation,
          speed: DEFAULT_ENGINE_CONFIG.agentWalkSpeed,
        });
        // Update pathIndex via proper Zustand set()
        useEngineStore.setState((state) => ({
          agents: {
            ...state.agents,
            ["claw-1"]: {
              ...state.agents["claw-1"],
              pathIndex: nextIndex,
            },
          },
        }));
      }
      return;
    }

    direction.normalize();
    const moveSpeed = DEFAULT_ENGINE_CONFIG.agentWalkSpeed;
    const step = Math.min(moveSpeed * delta, distance);
    const newPos = current.add(direction.multiplyScalar(step));

    const targetRotation = Math.atan2(direction.x, direction.z);
    const newRotation = THREE.MathUtils.lerp(
      currentAgent.rotation,
      targetRotation,
      0.1,
    );

    useEngineStore.getState().updateAgentPosition({
      id: "claw-1",
      position: [newPos.x, newPos.y, newPos.z],
      rotation: newRotation,
      speed: moveSpeed,
    });
  });

  if (!agent) return null;

  return (
    <group
      name="agent-claw-1"
      position={[agent.position[0], agent.position[1], agent.position[2]]}
      rotation={[0, agent.rotation, 0]}
      scale={2}
    >
      {/* Invisible hitbox for raycasting — <primitive> children (SkinnedMesh)
          are not in R3F's instance map, so rays pass through them. Event
          handlers go directly on this mesh (not the parent group) so R3F
          adds it to the interaction set and raycasts against its geometry. */}
      <mesh
        name="hitbox-claw-1"
        position={[0, 1, 0]}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={handlePoke}
      >
        <boxGeometry args={[1, 1, 1]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
      <primitive object={clonedScene} />

      {/* Bark bubble: floats above his head and tracks him around the world. */}
      {line && (
        <Html position={[0, 1.5, 0]} center zIndexRange={[20, 0]}>
          <p
            key={line}
            className="pointer-events-none w-max max-w-[220px] rounded-2xl rounded-bl-md bg-home-ink px-3.5 py-2 text-center font-body text-[12px] leading-snug font-medium text-home-cream shadow-[0_10px_24px_-12px_rgba(0,0,0,0.5)] [animation:poke-pop_0.28s_ease-out]"
          >
            {line}
          </p>
        </Html>
      )}
    </group>
  );
}

useGLTF.preload("/assets/lobster/lobster-mesh.glb");
useGLTF.preload("/assets/lobster/lobster-animations.glb");
